import React, { ReactNode, ReactElement } from 'react';
import { makeStyles, shorthands } from '@fluentui/react-components';
import { customTokens } from '../../../../styles';

const useClasses = makeStyles({
    tableContainer: {
        maxWidth: '100%',
        marginTop: '10px',
        marginBottom: '10px',
        overflowX: 'auto',
        boxSizing: 'border-box',
        '& table': {
            tableLayout: 'fixed',
            borderSpacing: '0',
            ...shorthands.borderTop('1px', 'solid', customTokens.colorNeutralBackground6),
            ...shorthands.borderLeft('1px', 'solid', customTokens.colorNeutralBackground6),
            wordBreak: 'normal',
        },
        '& th, & td': {
            ...shorthands.padding('5px'),
            verticalAlign: 'top',
            ...shorthands.borderBottom('1px', 'solid', customTokens.colorNeutralBackground6),
            ...shorthands.borderRight('1px', 'solid', customTokens.colorNeutralBackground6),
            boxSizing: 'border-box',
            minWidth: '10vw',
            width: 'auto',
        },
        '& th': {
            backgroundColor: customTokens.colorNeutralBackground2,
            fontWeight: 500,
            textAlign: 'left',
        },
        '& ul': {
            ...shorthands.margin('0'),
            paddingLeft: '20px',
        },
    },
});

const CreateElementsFromStringArray = (strings: string[]): ReactNode[] => {
    const elements: ReactNode[] = [];
    let currentList: ReactNode[] = [];
    let listItemContent: string[] = [];
    let inListItem = false;

    strings.forEach((str) => {
        switch (str) {
            case '<ul>':
                currentList = [];
                break;
            case '</ul>':
                elements.push(<ul key={elements.length}>{currentList}</ul>);
                currentList = [];
                break;
            case '<li>':
                inListItem = true;
                listItemContent = [];
                break;
            case '</li>':
                currentList.push(<li key={currentList.length}>{listItemContent.join('')}</li>);
                inListItem = false;
                break;
            case '<br>':
            case '<br/>':
                elements.push(<br key={elements.length} />);
                break;
            default:
                if (inListItem) listItemContent.push(str);
                else elements.push(str);
        }
    });

    return elements;
};

const parseThroughTable = (child: ReactNode): ReactNode => {
    if (React.isValidElement(child) && (child.props as { children?: ReactNode }).children) {
        const newChildren = replaceHTML((child.props as { children?: ReactNode }).children);
        return React.cloneElement(child as ReactElement, { children: newChildren });
    }
    return child;
};

const replaceHTML = (children: ReactNode): ReactNode[] => {
    if (Array.isArray(children)) {
        const hasStringChildren = children.some((child) => typeof child === 'string');
        return hasStringChildren
            ? CreateElementsFromStringArray(children as string[])
            : children.flatMap(parseThroughTable);
    } else {
        return [parseThroughTable(children)];
    }
};

interface OutputTableProps {
    children?: ReactNode;
}

export const OutputTable: React.FC<OutputTableProps> = ({ children }) => {
    const classes = useClasses();
    const cleanedChildren = replaceHTML(children as ReactElement[]);
    return (
        <div className={classes.tableContainer}>
            <table>{cleanedChildren}</table>
        </div>
    );
};
